import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";

import { CreateLiveChatOrQuestionMessage } from "../../../../../connection/save-chat-message";
import { toggleRegistrationModal } from "../../../../../store/actions/event/event-actions";
import { setModuleQuestions } from "../../../../../store/actions/event/questions-actions";
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import { TemplateClassNames } from "../../../../../types/template-layouts";
import { PageModule, SqsSocketTypes, RegFieldsEnum } from "../../../../../types/working-model";
import { getStorageItem } from "../../../../../utils/local-storage";
import InputSubmit from "./input-submit";

interface IAskQuestionsProps {
	module: PageModule;
	template?: TemplateClassNames;
	sessionUuid: string;
	onFocus: () => void;
	onBlur: () => void;
}

const AskQuestion: React.FC<IAskQuestionsProps> = ({ module, sessionUuid, onBlur, onFocus }) => {

	const params = useParams<any>();
	const language = params?.language || 'en';

	const dispatch = useDispatch();
	const { t } = useTranslation("session");

	const { event, sessions, channel, blProfileUser, questions } = useTypedSelector(state => ({
		event: state.LiveEventReducer.eventBundle?.event,
		sessions: state.LiveEventReducer.eventBundle?.sessions,
		channel: state.LiveEventReducer.eventBundle?.channel,
		blProfileUser: state.LiveEventReducer.blProfileUser,
		questions: state.QuestionsReducer.questions,
	}));

	const getSession = useCallback(() => {
		return sessions?.find(session => session.uuid === sessionUuid);
	}, [sessions, sessionUuid]);

	const session = getSession();
	if (!session || !channel) return null;
	return (
		<InputSubmit
			maxLength={150}
			onFocus={() => {
				const token = getStorageItem('Bl-Profile-Token');
				if (!token) {
					// show profile details modal and return
					dispatch(toggleRegistrationModal(sessionUuid));
				} else {
					onFocus();
				}
			}}
			onBlur={onBlur}
			onSubmitClick={async value => {
				if (!value.trim().length) return Promise.resolve({});
				const token = getStorageItem('Bl-Profile-Token');
				if (!token) {
					// show profile details modal and return
					dispatch(toggleRegistrationModal(sessionUuid));
					return Promise.resolve({ doNotClear: true });
				}
				// submit to microservices api
				// need to update this body and also the microservice to handle different message types
				const body = {
					message_type: SqsSocketTypes.QUESTION,
					question: value,
					first_name: blProfileUser?.profile?.[channel][RegFieldsEnum.first_name],
					last_name: blProfileUser?.profile?.[channel][RegFieldsEnum.last_name],
					profile: blProfileUser?.profile,
					event,
					channel,
					session: session.session,
					email: blProfileUser?.email,
					module_id: module.id,
					session_uuid: session.uuid,
					language,
				};
				const result = await CreateLiveChatOrQuestionMessage(session.session, body, token, SqsSocketTypes.QUESTION);
				if (result?.spam) {
					// Add the users message directly to their list.
					// It will not be sent to anyone else because the backend will not trigger the socket
					const copy = Array.from(new Map(questions[result.module_id]));
					copy.unshift([result.id, result]);
					const updated = new Map(copy);
					dispatch(setModuleQuestions({ [result.module_id]: updated }));
				}
				return Promise.resolve({});
			}}
			module={module}
			placeholder={t("What is on your mind")}
			buttonText={t("Ask question")}
		/>
	);
};

export default AskQuestion;
